import { Stack } from '@mui/material';
import { NodeToolbar, Position } from '@xyflow/react';

import { BulkSizeControls } from '@/components/DiagramBar/BulkSizeControls';
import {
  TileSelectionBar,
  VerticalDivider,
} from '@/components/DiagramBar/TileSelectionBar';
import { AlignmentPicker } from '@/components/diagrams/components/AlignmentPicker/AlignmentPicker';
import { DiagramColorPicker } from '@/components/diagrams/components/DiagramStylePickers/DiagramColorPicker';
import {
  DiagramMenuButton,
  DiagramMenuCheckbox,
  DiagramMenuDivider,
  DiagramMenuOptions,
  DiagramOptionPicker,
  type DiagramPickerOption,
} from '@/components/diagrams/components/DiagramStylePickers/DiagramOptionPicker';
import { DiagramShapeGlyph } from '@/components/diagrams/components/DiagramStylePickers/diagramStyleGlyphs';
import {
  ARROWHEAD_OPTIONS,
  ICON_SHAPE_OPTIONS,
  LINE_PATTERN_OPTIONS,
  LINE_ROUTE_OPTIONS,
  LINE_WEIGHT_OPTIONS,
  NODE_DISPLAY_OPTIONS,
  NODE_FILL_OPTIONS,
  NODE_ICON_OPTIONS,
  NODE_SHAPE_OPTIONS,
} from '@/components/diagrams/components/DiagramStylePickers/diagramStyleOptions';
import { TileEmphasisPicker } from '@/components/diagrams/components/TileEmphasisPicker/TileEmphasisPicker';
import {
  DiagramNodeShape,
  ICON_SHAPE_BY_NODE_SHAPE,
} from '@/components/diagrams/diagramStyle';
import { Button } from '@/components/form/baseInputs/Button';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { COLORS } from '@/styles/tokens/colors';

import { useDiagramEditing } from '../../contexts/diagramEditing.context';
import { useDiagramSelection } from '../../hooks/useDiagramSelection';
import {
  LINE_DEFAULT_COLOR,
  useDiagramColorOptions,
} from './diagramColorOptions';
import {
  DiagramLineToolbar,
  SELECTION_TOOLBAR_OFFSET,
} from './DiagramLineToolbar';
import { DiagramTileSettingsItems } from './DiagramTileSettings';

/** The trigger text when the selected elements have different values. */
const MIXED_LABEL = 'Mixed';

/** Returns the label of the option with `value`, or the mixed label. */
function getOptionLabel<T>(
  options: readonly DiagramPickerOption<T>[],
  value: T | null
) {
  return options.find((option) => option.value === value)?.label ?? MIXED_LABEL;
}

/**
 * Edits every selected node or connection with the shared diagram controls,
 * as in the Figma handoff. The toolbar of a node selection floats above the
 * nodes. Its controls depend on how the nodes draw: tiles have emphasis and
 * text settings, icons have an icon menu, and shapes have a shape menu. The
 * toolbar of a connection selection floats above the connection labels.
 */
export function DiagramSelectionBar() {
  const selection = useDiagramSelection();
  const { nodeColorOptions, lineColorOptions } = useDiagramColorOptions();
  const { notesEditorNodeID } = useDiagramEditing();
  const { edgeStyle } = selection;

  // The canvas shows no hint before the user selects an element. The notes
  // editor shows its own text toolbar.
  if (selection.selectedCount === 0 || notesEditorNodeID) return null;

  // Standalone text has no node style, so a selection of only text shows
  // the align controls, when it has more than one node, and Delete. A
  // divider shows only between two groups of controls.
  const showsNodeControls = selection.hasStyledNodes || selection.canAlign;
  const showsEdgeControls = selection.hasEdges && !selection.hasNodes;

  const bar = (
    <TileSelectionBar selectedCount={selection.selectedCount}>
      {showsNodeControls && (
        <>
          {selection.canAlign && (
            <AlignmentPicker
              onAlign={selection.align}
              onDistribute={selection.distribute}
              canDistribute={selection.canDistribute}
            />
          )}
          {selection.hasEmphasisNodes && (
            <TileEmphasisPicker
              currentEmphasis={selection.emphasis.value}
              isMixed={selection.emphasis.isMixed}
              onEmphasisChange={selection.setTileEmphasis}
            />
          )}
          {selection.hasShapeNodes && (
            <DiagramMenuButton
              label="Shape"
              trigger={
                <DiagramShapeGlyph
                  shape={
                    ICON_SHAPE_BY_NODE_SHAPE[
                      selection.nodeShape ?? DiagramNodeShape.Rounded
                    ]
                  }
                />
              }
            >
              <DiagramMenuOptions
                label="Shape"
                options={NODE_SHAPE_OPTIONS}
                value={selection.nodeShape}
                onChange={selection.setNodeShape}
              />
              <DiagramMenuDivider />
              <DiagramMenuOptions
                label="Fill"
                options={NODE_FILL_OPTIONS}
                value={selection.fill}
                onChange={selection.setFill}
              />
            </DiagramMenuButton>
          )}
          {selection.hasStyledNodes && (
            <DiagramColorPicker
              options={nodeColorOptions}
              value={selection.color}
              onChange={selection.setColor}
              allowCustom
            />
          )}
          {selection.hasIconNodes && (
            <DiagramMenuButton
              label="Icon"
              trigger={
                (
                  NODE_ICON_OPTIONS.find(
                    (option) => option.value === selection.icon
                  ) ?? NODE_ICON_OPTIONS[0]
                )?.icon
              }
            >
              <DiagramMenuOptions
                label="Icon"
                options={NODE_ICON_OPTIONS}
                value={selection.icon}
                onChange={selection.setIcon}
              />
              <DiagramMenuDivider />
              <DiagramMenuOptions
                label="Shape"
                options={ICON_SHAPE_OPTIONS}
                value={selection.shape.isMixed ? null : selection.shape.value}
                onChange={selection.setIconShape}
              />
            </DiagramMenuButton>
          )}
          {selection.hasStyledNodes && (
            <DiagramMenuButton
              label="Display as"
              trigger={getOptionLabel(
                NODE_DISPLAY_OPTIONS,
                selection.display.isMixed ? null : selection.display.value
              )}
            >
              <DiagramMenuOptions
                label="Display as"
                options={NODE_DISPLAY_OPTIONS}
                value={
                  selection.display.isMixed ? null : selection.display.value
                }
                onChange={selection.setDisplay}
              />
              {selection.hasTileNodes && <DiagramMenuDivider />}
              {selection.hasTileNodes && (
                <DiagramTileSettingsItems
                  shows={{
                    notes: selection.showsNotes,
                    footer: selection.showsFooter,
                  }}
                  onChange={selection.setTextVisibility}
                />
              )}
            </DiagramMenuButton>
          )}
          {selection.hasStyledNodes && (
            <>
              <VerticalDivider />
              <BulkSizeControls
                onIncrease={selection.increaseSize}
                onDecrease={selection.decreaseSize}
                canIncrease={selection.canIncreaseSize}
                canDecrease={selection.canDecreaseSize}
              />
            </>
          )}
        </>
      )}
      {showsEdgeControls && (
        <>
          <DiagramColorPicker
            options={lineColorOptions}
            value={edgeStyle.color || LINE_DEFAULT_COLOR}
            onChange={(color) => selection.setEdgeStyle({ color })}
            allowCustom
          />
          <DiagramOptionPicker
            label="Arrows"
            options={ARROWHEAD_OPTIONS}
            value={edgeStyle.arrowheads}
            onChange={(arrowheads) => selection.setEdgeStyle({ arrowheads })}
          />
          <DiagramMenuButton
            label="Style"
            trigger={
              <Stack direction="row" alignItems="center" gap={0.5}>
                {
                  LINE_PATTERN_OPTIONS.find(
                    (option) => option.value === edgeStyle.lineStyle
                  )?.icon
                }
                {getOptionLabel(LINE_PATTERN_OPTIONS, edgeStyle.lineStyle)}
              </Stack>
            }
          >
            <DiagramMenuOptions
              label="Style"
              options={LINE_PATTERN_OPTIONS}
              value={edgeStyle.lineStyle}
              onChange={(lineStyle) => selection.setEdgeStyle({ lineStyle })}
            />
            <DiagramMenuDivider />
            <DiagramMenuOptions
              label="Weight"
              options={LINE_WEIGHT_OPTIONS}
              value={edgeStyle.strokeWidth}
              onChange={(strokeWidth) =>
                selection.setEdgeStyle({ strokeWidth })
              }
            />
            {/* Not in the Figma handoff. The builder keeps the route
                shapes that the user can bend by hand. */}
            <DiagramMenuDivider />
            <DiagramMenuOptions
              label="Route"
              options={LINE_ROUTE_OPTIONS}
              value={edgeStyle.route}
              onChange={(route) => selection.setEdgeStyle({ route })}
            />
            <DiagramMenuDivider />
            <DiagramMenuCheckbox
              label="Show label"
              checked={selection.showsEdgeLabels}
              onChange={selection.setShowsEdgeLabels}
            />
          </DiagramMenuButton>
        </>
      )}
      {(showsNodeControls || showsEdgeControls) && <VerticalDivider />}
      <Button
        variant="transparent"
        size="xs"
        square
        aria-label="Delete"
        onClick={selection.removeSelection}
      >
        <Trash01Icon size={16} color={COLORS.FUNCTIONAL.ERROR.DEFAULT} />
      </Button>
    </TileSelectionBar>
  );

  return selection.hasNodes ? (
    <NodeToolbar
      nodeId={selection.nodeIDs}
      isVisible
      position={Position.Top}
      offset={SELECTION_TOOLBAR_OFFSET}
    >
      {bar}
    </NodeToolbar>
  ) : (
    <DiagramLineToolbar edgeIDs={selection.edgeIDs}>{bar}</DiagramLineToolbar>
  );
}
